import {
  mapBookingResponseToCheckedInPassengers,
  mapBookingResponseToNotCheckedInPassengers,
} from '@helper/passenger-mapper';
import { mapContactDetails } from '@helper/contact-details.mapper';
import { mapOffloadPaxListFromBooking } from '@helper/cancel-check-in.view.mapper';
import type { BookingResponse } from '@models/landing/api/api-response.model';
import {
  ContactTypeEnum,
  PassengerTypeEnum as ApiPassengerType,
  type ContactType,
} from '@models/landing/master-data/master-data.model';
import { PassengerTypeCode } from '@models/common/shared-enums.model';
import { PassengerTypeEnum } from '@models/landing/ui/select-passenger-model';
import type {
  ConfirmContactUpdateTarget,
  ConfirmNotificationTarget,
  ConfirmPnrMappedData,
} from '@models/landing/ui/confirm-pnr.model';
import type { NotificationRequest } from '@models/landing/api/notification.model';
import type { Airport, CodeTypeItem, MealsList } from '@models/master-data/master-data.model';
import type { CheckedInPax } from '@models/ui/pnr-data-model';
import { sortPassengersWithAssignedInfants } from '@shared/components/ssr-passenger.util';

const DEFAULT_NOTIFICATION_LOGICAL_FLIGHT_ID = 0;

const findContactByType = (
  contacts: NonNullable<BookingResponse['paxList'][number]['contactInfo']>,
  contactType: ContactType,
): NonNullable<BookingResponse['paxList'][number]['contactInfo']>[number] | undefined =>
  contacts.find((contact) => contact.contactType === contactType);

const resolveNotificationLogicalFlightId = (
  response: BookingResponse,
  resPaxId: number,
): number => {
  const logicalFlightId = response.flights.find((flight) =>
    flight.legs.some((leg) =>
      leg.pax.some((paxEntry) => paxEntry.resPaxId === resPaxId && paxEntry.boardingPass !== null),
    ),
  )?.logicalFlightID;

  if (!logicalFlightId) {
    return DEFAULT_NOTIFICATION_LOGICAL_FLIGHT_ID;
  }

  const parsed = Number(logicalFlightId);
  return Number.isFinite(parsed) ? parsed : DEFAULT_NOTIFICATION_LOGICAL_FLIGHT_ID;
};

export const mapNotificationTargetsFromBooking = (
  response: BookingResponse,
  checkedInPaxList: CheckedInPax[],
): Record<string, ConfirmNotificationTarget> => {
  const checkedInPaxIds = new Set(checkedInPaxList.map((pax) => pax.paxId));

  return response.paxList.reduce<Record<string, ConfirmNotificationTarget>>((targets, pax) => {
    const paxId = String(pax.resPaxId);
    if (!checkedInPaxIds.has(paxId)) {
      return targets;
    }

    targets[paxId] = {
      logicalFlightId: resolveNotificationLogicalFlightId(response, pax.resPaxId),
      resPaxId: pax.resPaxId,
      paxJourneyId: pax.paxJourneyId,
    };

    return targets;
  }, {});
};

/**
 * Picks one checked-in (non-infant) target for EmailAll / SmsAll / WAPPAll.
 * Backend *All endpoints broadcast from a single passenger context.
 */
export const resolveAllNotificationTarget = (
  targets: Record<string, ConfirmNotificationTarget>,
  checkedInPaxList: CheckedInPax[],
): ConfirmNotificationTarget | undefined => {
  const preferredPax = checkedInPaxList.find(
    (pax) => pax.passengerType !== PassengerTypeEnum.Infant && targets[pax.paxId] !== undefined,
  );

  if (preferredPax) {
    return targets[preferredPax.paxId];
  }

  return Object.values(targets)[0];
};

/** Builds Notification/Email|SMS|WAPP(+All) request body from a mapped target. */
export const buildConfirmNotificationRequest = (
  target: ConfirmNotificationTarget,
  contact: { emailId?: string; mobileNumber?: string },
): NotificationRequest => ({
  ...target,
  ...contact,
});

const mapContactUpdateTargets = (
  response: BookingResponse,
): Record<string, ConfirmContactUpdateTarget> => {
  if (!response.paxList?.length) {
    return {};
  }

  return response.paxList.reduce<Record<string, ConfirmContactUpdateTarget>>((targets, pax) => {
    const contacts = pax.contactInfo ?? [];
    const emailContact = findContactByType(contacts, ContactTypeEnum.Email);
    const phoneContact = findContactByType(contacts, ContactTypeEnum.MobilePhone);

    targets[String(pax.resPaxId)] = {
      resPaxId: pax.resPaxId,
      recordNumber: pax.recordNumber,
      paxJourneyId: pax.paxJourneyId,
      emailContactID: emailContact?.contactID ?? '',
      mobileContactID: phoneContact?.contactID ?? '',
    };

    return targets;
  }, {});
};

/**
 * Contact section shows only checked-in non-infant passengers.
 * Landing `mapContactDetails` maps the full paxList — confirm must filter first.
 */
const toConfirmContactBookingResponse = (
  response: BookingResponse,
  checkedInPaxList: CheckedInPax[],
): BookingResponse => {
  const checkedInPaxIds = new Set(checkedInPaxList.map((pax) => pax.paxId));

  return {
    ...response,
    paxList: response.paxList.filter(
      (pax) =>
        checkedInPaxIds.has(String(pax.resPaxId)) && pax.passengerType !== ApiPassengerType.Infant,
    ),
  };
};

/** Primary email from the full booking — independent of check-in status. */
export const resolvePrimaryPassengerEmail = (response: BookingResponse): string => {
  const paxList = response.paxList ?? [];
  if (paxList.length === 0) {
    return '';
  }

  const primaryPax = paxList.find((pax) => pax.isPrimaryPassenger) ?? paxList[0];
  const emailContact = findContactByType(primaryPax.contactInfo ?? [], ContactTypeEnum.Email);
  return emailContact?.contactField ?? '';
};

const toPassengerTypeCode = (passengerType: PassengerTypeEnum): number => {
  if (passengerType === PassengerTypeEnum.Infant) {
    return Number(PassengerTypeCode.Infant);
  }
  if (passengerType === PassengerTypeEnum.Child) {
    return Number(PassengerTypeCode.Child);
  }
  return Number(PassengerTypeCode.Adult);
};

interface SortableCheckedInPax {
  source: CheckedInPax;
  resPaxId: number;
  passengerType: number;
  isPrimaryPassenger?: boolean;
  travelsWithPassengerId?: unknown;
}

const sortCheckedInPaxList = (passengers: CheckedInPax[]): CheckedInPax[] => {
  if (passengers.length <= 1) {
    return passengers;
  }

  const sortable: SortableCheckedInPax[] = passengers.map((pax) => ({
    source: pax,
    resPaxId: pax.resPaxId ?? Number(pax.paxId),
    passengerType: toPassengerTypeCode(pax.passengerType),
    isPrimaryPassenger: pax.isPrimaryPassenger,
    travelsWithPassengerId:
      pax.passengerType === PassengerTypeEnum.Infant
        ? (pax.travelsWithPassengerId ?? pax.associatedInfantID)
        : pax.travelsWithPassengerId,
  }));

  return sortPassengersWithAssignedInfants(sortable).map((item) => item.source);
};

/**
 * Maps confirm-PNR API response to confirmation page domain data.
 * Contact UI details reuse landing `mapContactDetails` for checked-in non-infants;
 * API update IDs live in `contactUpdateTargets` keyed by paxId.
 */
export const mapConfirmPnrData = (
  response: BookingResponse,
  codeTypeList: CodeTypeItem[],
  airports: Airport[],
  mealsList: MealsList,
): ConfirmPnrMappedData => {
  const checkedInPaxList = sortCheckedInPaxList(
    mapBookingResponseToCheckedInPassengers(response, codeTypeList, airports, mealsList),
  );
  const notCheckedInPaxList = mapBookingResponseToNotCheckedInPassengers(
    response,
    codeTypeList,
    mealsList,
  );
  const contactSource = toConfirmContactBookingResponse(response, checkedInPaxList);

  return {
    bookingReference: response.confirmationNumber ?? '',
    checkedInPaxList,
    notCheckedInPaxList,
    contactDetails: mapContactDetails(contactSource),
    offloadPaxList: mapOffloadPaxListFromBooking(response, checkedInPaxList),
    notificationTargets: mapNotificationTargetsFromBooking(response, checkedInPaxList),
    contactUpdateTargets: mapContactUpdateTargets(contactSource),
    primaryPassengerEmail: resolvePrimaryPassengerEmail(response),
  };
};
